import React from 'react';
import {useTranslation} from 'react-i18next';
import FileIcon from '@mui/icons-material/DescriptionOutlined';
import Typography from '@mui/material/Typography';
import FolderIcon from 'components/icons/FolderIcon';
import makeStyles from '@mui/styles/makeStyles';
import {IDiplomatFile} from 'types/DiplomatFile';
import {
   DEFAULT_PATH,
   getArrayOfBreadcrumbs,
   getRootFolderPath,
   isRootFolderPath,
   trimPath,
   UPFOLDER_NAME,
} from 'common/stringUtils';
import {directoryListingContext} from 'features/layout/CommonPage/panels/TransportPanel/components/common/DirectoryListingProvider';
import {formatDateTimeStrict} from 'common/dateUtils';
import prettyBytes from 'pretty-bytes';
import {GridColDef, GridValueFormatterParams, DataGridPro} from '@mui/x-data-grid-pro';
import i18n from 'i18n';
import {directoryComparator} from 'common/comparators';
import {ImportRemoteMenu} from 'features/layout/CommonPage/panels/TransportPanel/components/common/ImportRemoteMenu';
import {useSelector} from 'react-redux';
import {loginSelectors} from 'features/login/redux';
import {UNIX_SEP, WINDOWS_SEP} from 'common/constants';
import {transportDataGetterContext} from 'features/layout/CommonPage/panels/TransportPanel/components/common/transportDataGetterContext';
import {isString} from 'lodash';

type DirectoryContentsListProps = {
   data?: IDiplomatFile[];
   path?: string;
   directoriesOnly?: boolean;
   request?: (path?: string) => void;
   selected?: IDiplomatFile;
   setSelected?: (path: IDiplomatFile) => void;
};

const useStyles = makeStyles((theme) => ({
   list: {
      border: '1px solid',
      borderColor: theme.palette.divider,
      padding: 0,
      overflow: 'auto',
      height: 300,
   },
   subheader: {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.background.paper,
   },
   link: {
      // cursor: 'pointer',
   },
}));

const NAME_FIELD = 'name';

const getDefaultFolder = (path: string = DEFAULT_PATH) =>
   ({
      directory: true,
      name: UPFOLDER_NAME,
      path,
      fileId: '-1',
      exists: true,
      // length: 0,
      // modifyTime: 0,
      parent: false,
      icon: '',
   } as IDiplomatFile);
const DEFAULT_UPFOLDER = getDefaultFolder();

export const columns: GridColDef[] = [
   {
      field: NAME_FIELD,
      headerName: i18n.t('Name'),
      flex: 300,
      renderCell(params) {
         const file = params.row;
         return (
            <>
               {file.directory ? <FolderIcon /> : <FileIcon />} {file.name}
            </>
         );
      },
      sortComparator: function sortComparator(v1, v2, cp1, cp2) {
         const row1 = cp1.api.getRow(cp1.id);
         const row2 = cp2.api.getRow(cp2.id);
         const sortModel = cp1.api.getSortModel();
         const sortOrder = sortModel.find((s: any) => s.field === NAME_FIELD)?.sort;

         // console.debug('sortComparator', v1, v2, cp1, cp2, row1, row2);
         const result = directoryComparator(
            v1 as string,
            v2 as string,
            row1?.directory,
            row2?.directory,
            sortOrder,
            UPFOLDER_NAME
         );
         return result; //
      },
   },
   {
      field: 'modifyTime',
      headerName: i18n.t('Modified'),
      flex: 200,
      valueFormatter: (params: GridValueFormatterParams) =>
         params.value && formatDateTimeStrict(Number(params.value)),
   },
   {
      field: 'length',
      headerName: i18n.t('Size'),
      flex: 100,
      // valueGetter: (params) => params.row.date,
      valueFormatter: (params: GridValueFormatterParams) =>
         params.value && prettyBytes(Number(params.value)).toUpperCase(),
      sortComparator(v1, v2, cp1, cp2) {
         const row1 = cp1.api.getRow(cp1.id);
         const row2 = cp2.api.getRow(cp2.id);
         // const sortModel = cp1.api.getSortModel();
         // const sortOrder = sortModel.find((s) => s.field === NAME_FIELD)?.sort;

         // console.debug('sortComparator length', v1, v2, cp1, cp2, row1, row2);
         return row1?.length - row2?.length;
      },
   },
];

const getUpfolderPath = (path?: string) => {
   const breadcrumbs = getArrayOfBreadcrumbs(path); // path?.slice(0, findLastIndex(path, WINDOWS_SEP) + 1);
   const upFolderPath =
      breadcrumbs[breadcrumbs.length - 2]?.bcPath ||
      (isRootFolderPath(path) ? undefined : getRootFolderPath(path));
   console.debug('Dir bread', breadcrumbs, upFolderPath);

   return upFolderPath;
};

export const DirectoryContentsList = (props: DirectoryContentsListProps) => {
   const {data, directoriesOnly} = props;

   const isReviewer = useSelector(loginSelectors.isReviewer);
   const contextValues = React.useContext(directoryListingContext);
   let {path, request, selected, setSelected} = contextValues || props;
   const {partner, anonymous, path: basePath} = React.useContext(transportDataGetterContext);
   const nonAnonymWithinTransaction = !partner && !anonymous;

   const {t} = useTranslation();
   // const classes = useStyles();

   const rows = React.useMemo(() => {
      const upFolderPath = getUpfolderPath(path || basePath);
      const upFolder = {
         directory: true,
         name: UPFOLDER_NAME,
         path: upFolderPath,
         fileId: '-1',
         exists: true,
         // length: 0,
         // modifyTime: 0,
         parent: false,
         icon: '',
      };
      const rows = data ? [...data] : [];
      console.debug('rows', upFolderPath, nonAnonymWithinTransaction, basePath);
      isString(upFolderPath) &&
         (nonAnonymWithinTransaction ? trimPath(basePath).length <= upFolderPath.length : true) &&
         // @ts-ignore
         rows.unshift(upFolder);

      return rows;
   }, [data, path, nonAnonymWithinTransaction, basePath]);

   const onUpdate = React.useCallback(() => {
      request?.(path);
   }, [request, path]);

   return (
      <>
         {(!data || !data.length) && <Typography>{t('Directory is empty.')}</Typography>}
         {!isReviewer && data && (
            <ImportRemoteMenu
               listing={data}
               path={path}
               selected={selected?.path}
               onUpdate={onUpdate}
            />
         )}
         <div style={{height: 500, width: '100%'}}>
            <DataGridPro
               loading={!data}
               density="compact"
               getRowId={(row) => row.path}
               rows={rows}
               columns={columns}
               selectionModel={selected?.path ? [selected.path] : []}
               // pageSize={10}
               hideFooter
               hideFooterPagination
               hideFooterRowCount
               disableMultipleSelection
               disableSelectionOnClick
               disableVirtualization
               initialState={{
                  sorting: {
                     sortModel: [{field: NAME_FIELD, sort: 'asc'}],
                  },
               }}
               onRowClick={(params, ev, details) => {
                  console.debug(params, ev, details, selected);
                  if (selected?.path !== params.id) {
                     const selectedFile = rows.find((d) => d.path === params.id) as IDiplomatFile;
                     setSelected?.(selectedFile);
                     details.api?.selectRow(params.id, false, true);
                  } else {
                     setSelected?.(getDefaultFolder(path));
                     details.api?.selectRow(params.id, true, true);
                  }
               }}
               onRowDoubleClick={(params, ev, details) => {
                  ev.preventDefault();
                  ev.stopPropagation();
                  const file = params.row;
                  setSelected?.(file as IDiplomatFile);
                  details.api?.selectRow(params.id, false, true);
                  file.directory && request?.(file.path.trim());
               }}
            />
         </div>
         {/* <List
            className={classes.list}
            dense
            subheader={
               <ListSubheader className={classes.subheader} inset>
                  {path} 
               </ListSubheader>
            }
         >
            <ListItemButton
               // className={classes.link}
               onDoubleClick={() => request?.(upFolderPath)}
            >
               <ListItemIcon>
                  <FolderIcon />
               </ListItemIcon>
               <ListItemText primary=".." />
            </ListItemButton>
            <Divider />
            
            {data
               .sort((a, b) => Number(b.directory) - Number(a.directory))
               .map((file) => (
                  <React.Fragment key={file.name}>
                     <ListItemButton
                        // className={file.directory ? classes.link : undefined}
                        onDoubleClick={file.directory ? () => request?.(file.path) : undefined}
                        onClick={() => {
                           if (!directoriesOnly || (directoriesOnly && file.directory)) {
                              setSelected?.(file.path);
                           }
                        }}
                        selected={file.path === selected}
                     >
                        <ListItemIcon>
                           {file.directory ? <FolderIcon /> : <FileIcon />}
                        </ListItemIcon>
                        <ListItemText
                           primary={file.name}
                           secondary={`${formatDateTime(+file.modifyTime)} ${prettyBytes(
                              +file.length
                           )}`}
                        />
                     </ListItemButton>
                     <Divider />
                  </React.Fragment>
               ))}
         </List> */}
      </>
   );
};
